import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    Grid,
    IconButton,
    Slide,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import CloseIcon from "@mui/icons-material/Close";
import { Cancel, ModeOutlined } from "@mui/icons-material";
import { OrderItemDTO, OrderItemsPriceUpdateRequest } from "../../../redux/slice/order/OrderSlice";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import { useForm } from "react-hook-form";

import { priceFormatter } from "../../../utils/Utility";
import CustomTable, { Row as CustomTableRow } from "../../common/CustomTable";
import { Column } from "../../../redux/slice/commonSlice";
import { useModalStyles } from "../../common/useModalStyles";
import ConfirmationActionModal from "../../common/ConfirmationActionModal";

interface EditOrderAmountPopupProps {
    open: boolean;
    handleClose: () => void;
    orderItems: OrderItemDTO[];
    onUpdate: (payload: OrderItemsPriceUpdateRequest) => void;
}

interface GroupedItem {
    id: string;
    size: string;
    totalQuantity: number;
    orderItemPrice: number;
    productItemId: number;
    originalItems: OrderItemDTO[];
    isEditing: boolean;
    isCommissionPaid: boolean;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="down" ref={ref} {...props} />;
});

const EditOrderAmountPopup: React.FC<EditOrderAmountPopupProps> = ({
    open,
    handleClose,
    orderItems,
    onUpdate,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {
        tokens,
        paperSx,
        headerSx,
        iconBoxSx,
        bodySx,
        footerSx,
        closeIconSx,
        inputFieldSx,
        primaryBtnSx,
        cancelBtnSx,
        isDark,
    } = useModalStyles();

    const modalTableSx = {
        width: "100%",
        border: `1px solid ${tokens.border}`,
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: isDark ? "rgba(255,255,255,0.02)" : tokens.cardBg,
        "& > div": { mt: 0 },
        "& .MuiTableContainer-root": { borderRadius: 0 },
        "& .MuiTable-root": { background: "transparent !important" },
        "& .MuiTableCell-head": {
            background: `${isDark ? "rgba(255,255,255,0.03)" : tokens.cardBg} !important`,
            borderBottom: `1px solid ${tokens.border}`,
        },
        "& .MuiTableCell-head .MuiTypography-root": {
            color: `${tokens.textSecondary} !important`,
            fontWeight: 600,
            fontSize: "0.78rem",
        },
        "& .MuiTableCell-body": {
            borderBottom: `1px solid ${tokens.border}`,
            color: tokens.textPrimary,
        },
        "& .MuiTableRow-root:last-child .MuiTableCell-body": {
            borderBottom: "none",
        },
    };

    const { control, handleSubmit, setValue, watch } = useForm();
    const [groupedItems, setGroupedItems] = useState<GroupedItem[]>([]);
    const [showCommissionPaidWarning, setShowCommissionPaidWarning] = useState<boolean>(false);
    const [affectedItems, setAffectedItems] = useState<string[]>([]);
    const [pendingPayload, setPendingPayload] = useState<any>(null);

    const watchedValues = watch();

    const hasValidValues = useMemo(() => {
        return groupedItems.some(group => {
            const fieldName = `amount_${group.productItemId}`;
            const value = watchedValues[fieldName];

            if (value === undefined || value === null || value === '') {
                return false;
            }

            const numValue = typeof value === 'string' ? parseFloat(value) : value;
            const currentPrice = group.orderItemPrice;

            return !isNaN(numValue) && numValue >= 0 && numValue !== currentPrice;
        });
    }, [groupedItems, watchedValues]);

    useEffect(() => {
        if (open && orderItems) {
            const groups: { [key: number]: GroupedItem } = {};
            orderItems.forEach((item) => {
                const orderItemPrice = item.orderItemPrice || 0;

                if (!groups[item.productItemId]) {
                    groups[item.productItemId] = {
                        id: item.productItemId.toString(),
                        size: item.size,
                        totalQuantity: 0,
                        orderItemPrice: orderItemPrice,
                        productItemId: item.productItemId,
                        originalItems: [],
                        isEditing: false,
                        isCommissionPaid: item.isCommissionPaid || false
                    };
                }
                groups[item.productItemId].totalQuantity += item.quantity || 1;
                groups[item.productItemId].originalItems.push(item);
            });

            const groupedArray = Object.values(groups);
            groupedArray.forEach(group => {
                setValue(`amount_${group.productItemId}`, '');
            });
            setGroupedItems(groupedArray);
        }
    }, [open, orderItems, setValue]);

    const handleRowClick = useCallback((productItemId: number) => {
        setGroupedItems((prev) =>
            prev.map((item) =>
                item.productItemId === productItemId ? { ...item, isEditing: true } : item
            )
        );
    }, []);

    const onSubmit = (data: any) => {

        const payloadItems: any[] = [];

        groupedItems.forEach(group => {
            const fieldName = `amount_${group.productItemId}`;
            const newPriceValue = data[fieldName];

            if (newPriceValue === undefined || newPriceValue === null || newPriceValue === '') {
                return;
            }

            let newPrice: number;
            if (typeof newPriceValue === 'string') {
                newPrice = parseFloat(newPriceValue);
            } else if (typeof newPriceValue === 'number') {
                newPrice = newPriceValue;
            } else {
                return;
            }

            const currentPrice = group.orderItemPrice;


            if (!isNaN(newPrice) && newPrice >= 0 && newPrice !== currentPrice) {
                payloadItems.push({
                    productItemId: group.productItemId,
                    currentPrice: currentPrice,
                    newPrice: newPrice
                });
            }
        });

        const orderId = orderItems[0]?.orderId;
        if (!orderId) {
            return;
        }

        const payload = {
            orderId: orderId,
            orderItems: payloadItems
        };

        const commissionPaidItems = groupedItems.filter(group => {
            const fieldName = `amount_${group.productItemId}`;
            const newPriceValue = data[fieldName];
            const isModified = payloadItems.some(p => p.productItemId === group.productItemId);
            return isModified && group.isCommissionPaid;
        }).map(group => group.size);

        if (commissionPaidItems.length > 0) {
            setAffectedItems(commissionPaidItems);
            setPendingPayload(payload);
            setShowCommissionPaidWarning(true);
            return;
        }

        onUpdate(payload);
        handleClose();
    };

    const handleCommissionWarningConfirm = () => {
        if (pendingPayload) {
            onUpdate(pendingPayload);
            handleClose();
        }
        setShowCommissionPaidWarning(false);
        setPendingPayload(null);
        setAffectedItems([]);
    };

    const handleCommissionWarningClose = () => {
        setShowCommissionPaidWarning(false);
        setPendingPayload(null);
        setAffectedItems([]);
    };

    const columns: Column[] = [
        { id: "size", label: "Ordered Size" },
        { id: "totalQuantity", label: "Total Quantity" },
        { id: "currentPrice", label: "Current Price" },
        { id: "newPrice", label: "New Price" },
    ];

    const rows: CustomTableRow[] = useMemo(() => {
        return groupedItems.map((group) => ({
            id: group.id,
            size: group.size,
            totalQuantity: group.totalQuantity,
            currentPrice: priceFormatter(group.orderItemPrice),
            newPrice: (
                <Box onClick={() => handleRowClick(group.productItemId)} sx={{ cursor: "pointer" }}>
                    <Box onClick={(e) => e.stopPropagation()} sx={inputFieldSx()}>
                        <CustomCurrencyField
                            control={control}
                            controllerName={`amount_${group.productItemId}`}
                            label=""
                            size="small"
                            rules={{
                                min: {
                                    value: 0,
                                    message: "Price cannot be less than 0"
                                },
                                validate: (value: any) => {
                                    if (value === undefined || value === null || value === '') {
                                        return true;
                                    }

                                    const numValue = typeof value === 'string' ? parseFloat(value) : value;
                                    const currentPrice = group.orderItemPrice;

                                    if (!isNaN(numValue) && numValue === currentPrice) {
                                        return "New price cannot be the same as current price";
                                    }

                                    return true;
                                }
                            }}
                            sx={{
                                "& .MuiInputAdornment-root": { color: tokens.textSecondary },
                            }}
                        />
                    </Box>
                </Box>
            ),
        }));
    }, [groupedItems, control, handleRowClick, inputFieldSx, tokens.textSecondary]);

    return (
        <>
            <Dialog
                open={open}
                TransitionComponent={Transition}
                keepMounted
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    elevation: 0,
                    sx: paperSx(isMobile),
                }}
                BackdropProps={{
                    sx: {
                        backgroundColor: isDark ? "rgba(0,0,0,0.78)" : "rgba(15,23,42,0.5)",
                        backdropFilter: "blur(6px)",
                    },
                }}
            >
                <Box sx={{ ...headerSx, position: "relative" }}>
                    <Box sx={iconBoxSx(tokens.accent)}>
                        <ModeOutlined sx={{ fontSize: 24, color: tokens.accent }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, pr: 4 }}>
                        <Typography
                            sx={{
                                fontSize: "1.05rem",
                                fontWeight: 700,
                                color: tokens.textPrimary,
                                letterSpacing: "-0.01em",
                                lineHeight: 1.3,
                            }}
                        >
                            Edit Item Price
                        </Typography>
                        <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.25 }}>
                            Enter new prices per size; unchanged rows are skipped.
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small" sx={closeIconSx} aria-label="Close">
                        <CloseIcon sx={{ fontSize: "1.1rem" }} />
                    </IconButton>
                </Box>

                <form onSubmit={handleSubmit(onSubmit)} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                    <Box sx={{ ...bodySx, pt: 2 }}>
                        <Grid container spacing={2} sx={{ margin: 0, width: "100%" }}>
                            <CustomTable
                                columns={columns}
                                rows={rows}
                                disablePagination={true}
                                sx={modalTableSx}
                            />
                        </Grid>
                    </Box>

                    <Box sx={footerSx}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "flex-end", width: "100%" }}>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={!hasValidValues}
                                startIcon={<ModeOutlined sx={{ fontSize: "1rem !important" }} />}
                                sx={primaryBtnSx}
                            >
                                Update
                            </Button>
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={handleClose}
                                startIcon={<Cancel sx={{ fontSize: "1rem !important" }} />}
                                sx={cancelBtnSx}
                            >
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                </form>
            </Dialog>

            <ConfirmationActionModal
                open={showCommissionPaidWarning}
                onClose={handleCommissionWarningClose}
                onConfirm={handleCommissionWarningConfirm}
                title="Service Fee already paid"
                subtitle="Confirm before updating prices"
                message={`The service fee has already been paid for the following ordered sizes: ${affectedItems.join(", ")}. Do you want to proceed with the price update?`}
                confirmText="Confirm"
                cancelText="Cancel"
            />
        </>
    );
};

export default EditOrderAmountPopup;
